import { Activity, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import MobileSection from './MobileSection';
import ProfileSection from './ProfileSection';
import OrgSection from './OrgSection';
import LocalizationSection from './LocalizationSection';
import MegaMenuSection from './MegaMenuSection';
import FullScreenSection from './FullScreenSection';
import NotificationSection from './NotificationSection';

//import ProjectsSearchDropdown from 'ui-component/dropdowns/ProjectsSearchDropdown';
import ProjectsSearchBarDropdown from 'ui-component/dropdowns/ProjectsSearchBarDropdown';

import { useProjects } from 'contexts/projects/useProjects';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';

// assets
import { IconMenu2 } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';

import { useNavigate } from 'react-router-dom';

import { hasRole, ROLES } from '../../../constants/roles';


// ==============================|| MAIN NAVBAR / HEADER ||============================== //

export default function Header() {
  const theme = useTheme();
  const navigate = useNavigate();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { projects, projectTypes, activeProjectId, selectProject } = useProjects();

  // The chooser was showing the raw project-type CODE as its subtitle and a randomly generated
  // status. Resolve the code the way the projects list does, and read the status the pipeline
  // actually reported.
  const projectTypeTitles = useMemo(
    () => Object.fromEntries((projectTypes ?? []).map((t) => [t.code, t.title])),
    [projectTypes]
  );

  const {
    state: { menuOrientation }
  } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const { appUser } = useAuth();

  return (
    <>
      {/* logo & toggler button */}
<Box sx={{ width: downMD ? 'auto' : 268, display: 'flex', alignItems: 'center' }}>
  <Box component="span" sx={{ ml:-0.5, display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
    <LogoSection />
  </Box>
  <Activity mode={!isHorizontal ? 'visible' : 'hidden'}>
    <Avatar
      variant="rounded"
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        overflow: 'hidden',
        transition: 'all .2s ease-in-out',
        color: theme.vars.palette.secondary.dark,
        background: theme.vars.palette.secondary.light,
        '&:hover': {
          color: theme.vars.palette.secondary.light,
          background: theme.vars.palette.secondary.dark
        },
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.secondary.main,
          background: theme.vars.palette.dark.main,
          '&:hover': {
            color: theme.vars.palette.secondary.light,
            background: theme.vars.palette.secondary.main
          }
        })
      }}
      onClick={() => handlerDrawerOpen(!drawerOpen)}
    >
      <IconMenu2 stroke={1.5} size="20px" />
    </Avatar>
  </Activity>
</Box>

      {/* header search */}




      <Box sx={{ mx: 3, flexGrow: 1, minWidth: 200, maxWidth: 600 }}>
        <ProjectsSearchBarDropdown
          projects={projects.map(p => ({
            id: p.id,
            name: p.name,
            // Falls back to the code rather than to nothing: an unresolved type is better shown
            // as its code than silently blank, which hides a missing lookup row.
            subtitle: projectTypeTitles[p.type] ?? p.type,
            // Only a status the pipeline genuinely reported. 'unknown' means no run has produced
            // one, and saying nothing is more honest than inventing a label for it.
            status: p.pipelineStatus && p.pipelineStatus !== 'unknown' ? p.pipelineStatus : undefined
          }))}
          activeProjectId={activeProjectId}
          onSelectProject={selectProject}
          onNewProject={() => navigate('/projects?action=add')}
          onViewAll={() => navigate('/projects')}
        />
      </Box>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />



      {/* mega-menu */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <MegaMenuSection />
      </Box>

      {/* live customization & localization */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <LocalizationSection />
      </Box>

      {/* notification */}
      <NotificationSection />

      {/* full sceen toggler */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <FullScreenSection />
      </Box>

      {/* org */}
      {hasRole(appUser?.roles, ROLES.ORG_ADMIN) ? (
        <OrgSection />
      ) : (
        <Box sx={{ ml: 2 }}>
          <Chip
            label={<Typography variant="h4" component="span" color="text.secondary" sx={{ fontWeight: 400 }}>{appUser && appUser.orgName}</Typography>}
            color='secondary'
            sx={{
              borderRadius: 1,
              '& .MuiChip-label': {
                padding: 1
              }
            }}
          />
        </Box>
      )}

      {/* profile */}
      <ProfileSection />

      {/* mobile header */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <MobileSection />
      </Box>
    </>
  );
}
